import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

import { AccessReportsService } from "@app/access-reports";
import {
  AccessReportRunStatusChangeNotificationMessagePayload,
  AccessReportsDashboardResponseModel,
  AccessReportStatusEnum,
  AccessReportUsageReportChangeNotificationMessagePayload,
  UsageReport
} from "@app/access-reports/dashboard";
import { AccessReport, handleFileLinkClick } from "@app/access-reports/shared";
import { RoleRestricted, Roles, UserContext, UserInfo } from "@app/user";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { formatUnconvertedDateCustom, LoadingSpinner, Page, PageHeader, PageHeaderLeft, PageHeaderRight, PageInner, PageSection, TooltipWrapper } from "@app/shared";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { preventDefaultMouseEvent } from "@app/shared/utility";
import { ColumnConfig, SortDirections, Waffles } from "@app/shared/waffles";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { toast, ToastOptions } from "react-toastify";
import { FlyoutLink } from "@app/shared/Flyouts";
import { JSX } from "react";

type AccessReportsDashboardProps = RouteComponentProps<unknown> & NotificationContextProps;

interface AccessReportsDashboardState {
  accessReports: AccessReport[];
  isFiltered: boolean;
  loading: boolean;
  offset: number;
  reportSearch: string;
  sidePanelVisible: boolean;
  sortDirection: SortDirection;
  sortedColumn: keyof AccessReportsPanelWaffleToppings;
  totalCount: number;
  includeArchived: boolean;
}

class AccessReportsPanelWaffleToppings {
  public clone: React.ReactNode;
  public companyName: string;
  public createdBy: string;
  public dateCreated: string;
  public download: React.ReactNode;
  public internalClientId: string;
  public key: string;
  public product: string;
  public reportTitle: React.ReactNode;
  public status: string;

  constructor(
    key: string,
    reportTitle: React.ReactNode,
    internalClientId: string,
    product: string,
    companyName: string,
    dateCreated: string,
    createdBy: string,
    status: string,
    download: React.ReactNode,
    clone: React.ReactNode
  ) {
    /* eslint-disable sort-keys */
    this.key = key;
    this.reportTitle = reportTitle;
    this.internalClientId = internalClientId;
    this.product = product;
    this.companyName = companyName;
    this.dateCreated = dateCreated;
    this.createdBy = createdBy;
    this.status = status;
    this.download = download;
    this.clone = clone;
    /* eslint-enable */
  }
}

class AccessReportsDashboard extends React.Component<AccessReportsDashboardProps, AccessReportsDashboardState> {
  private readonly accessReportsService = new AccessReportsService();
  private readonly batchCount: number = 100;


  constructor(props: AccessReportsDashboardProps) {
    super(props);

    this.state = {
      accessReports: [],
      includeArchived: false,
      isFiltered: false,
      loading: true,
      offset: 0,
      reportSearch: "",
      sidePanelVisible: false,
      sortDirection: SortDirection.Unknown,
      sortedColumn: null,
      totalCount: 0,

    };
  }

  public async componentDidMount() {
    await this.fetchAccessReports();

    this.props.subscribe(this.handleNotifications);
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);
  }

  public render() {
    const pageTitle = "Network Access Report Dashboard";
    return (
      <UserContext.Consumer>
        {(user: UserInfo) => (
          <Page className="bg-white" documentTitle="Access Reports">
            <PageInner>
              <PageSection>
                <div className="row">
                  <div className="col-12">
                    <PageHeader title={pageTitle}>
                      <PageHeaderLeft>
                        <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>

                          <span className="ml5">
                            <i className="fa fa-angle-double-up" /> <a href="#" className={(this.state.loading) ? "home-link-disabled" : ""} id="access-reports-dashboard__dashboard--archive" onClick={this.setArchiveMode}>{this.state.includeArchived ? "Unarchived" : "Archived"}</a>
                          </span>

                        </RoleRestricted>
                      </PageHeaderLeft>
                      <PageHeaderRight>
                        <Link
                          className="qes-button qes-button-primary float-right"
                          id="access-reports-dashboard__dashboard--request-new-report"
                          type="button"
                          to={{
                            pathname: `${this.props.match.path}/wizard/1`,
                          }}
                        >
                          Request a New Report
                        </Link>
                      </PageHeaderRight>
                    </PageHeader>
                  </div>
                </div>

                {this.state.loading ?
                  <div className="row">
                    <div className="col-12">
                      <div className="text-center">
                        <LoadingSpinner />
                      </div>
                    </div>
                  </div> :
                  this.state.totalCount > 0 ?
                    <React.Fragment>
                      {this.state.totalCount > 1 &&
                        <div className="row">
                          <div className="col-12">
                            <div className="card card-static">
                              <div className="card-body">
                                <div className="row">
                                  <div className="col-10">
                                    <QESTextInput
                                      containerClassName="access-reports-dashboard-filter-input"
                                      testId="access-reports-dashboard__dashboard--search-input"
                                      label="Search Previous Reports"
                                      onChange={(event) => this.onSearchChange(event)}
                                      placeholder="Search Internal Client Identifiers, dates, titles..."
                                      value={this.state.reportSearch}
                                    />
                                  </div>

                                  <div className="col-2">
                                    <div id="access-reports-dashboard-filter-buttons">
                                      <QESButton
                                        qesTestId="access-reports-dashboard__dashboard--search"
                                        qesType="secondary"
                                        onClick={() => this.fetchAccessReports(false, true)}
                                        className="font-weight-bold"
                                      >
                                        Search
                                      </QESButton>

                                      <QESButton
                                        qesTestId="access-reports-dashboard__dashboard--clear"
                                        qesType="secondary"
                                        onClick={() => this.onClearAllClick()}
                                        className="font-weight-bold"
                                      >
                                        Clear
                                      </QESButton>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      }

                      {this.state.accessReports.length > 0 ? (
                        <>
                          <div className="row">
                            <div className="col-12">
                              <Waffles<AccessReportsPanelWaffleToppings>
                                bulkActionBar={user.roles.includes(Roles.ACCESS_REPORTS_ADMIN) && (
                                  <span className="bulk-action-group">
                                    <FlyoutLink
                                      classNames="qes-button-secondary align-self-center p-0"
                                      content={{
                                        body: this.renderUserReportSidePanel(user?.clientId, user?.displayName),
                                        title: "Usage Report",
                                      }}
                                      panelId="access-reports-dashboard__dashboard--create-usage-report"
                                      isVisible={this.state.sidePanelVisible}
                                      size="20"
                                      testId="access-reports-dashboard__dashboard--create-usage-report-link"
                                      onClose={this.hideSidePanel}
                                    >
                                      <i className="fa fa-cloud-download mr-1" />
                                      Create Usage Report
                                    </FlyoutLink>
                                  </span>
                                )}
                                columns={this.getColumnConfig(user)}
                                customSort={this.customSort}
                                data={this.buildWaffleToppings(this.state.accessReports) || []}
                                fullWidth={true}
                                passThroughSorting={true}
                                testId="access-reports-dashboard__dashboard--all-reports"
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="row">
                          <div className="col-12">
                            <div className="text-center text-muted">
                              There are no files available.
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment> :
                    <div className="row">
                      <div className="col-12">
                        <div className="text-center text-muted">
                          There are no files available.
                        </div>
                      </div>
                    </div>
                }

                {(this.state.accessReports.length < this.state.totalCount && !this.state.isFiltered) && (
                  <div className="row">
                    <div className="col-12">
                      <div className="text-center">
                        <QESButton
                          qesTestId="access-reports-dashboard__dashboard--load-more"
                          className="mt-3"
                          onClick={() => this.fetchAccessReports()}
                        >
                          Load More
                        </QESButton>
                      </div>
                    </div>
                  </div>
                )}
              </PageSection>
            </PageInner>
          </Page>
        )}
      </UserContext.Consumer>
    );
  }

  public registerTriggerClose = (triggerClose: () => void): void => {
    if (!!triggerClose && !this.state.sidePanelVisible) {
      this.setState({
        sidePanelVisible: true,
      });
    }
  }

  public handleUsageReportToast = (status: AccessReportStatusEnum): void => {
    toast.dismiss();

    const toastOptions: ToastOptions = {
      closeOnClick: true,
      containerId: "z-index-9999",
    };

    switch (status) {
      case AccessReportStatusEnum.Canceled:
        toast.error("Your usage report was canceled", toastOptions);
        break;
      case AccessReportStatusEnum.Created:
        toast.info("Your usage report has been created", toastOptions);
        break;
      case AccessReportStatusEnum.Failed:
        toast.error("Your usage report has failed", toastOptions);
        break;
      case AccessReportStatusEnum.Running:
        toast.info("Your usage report is being processed", toastOptions);
        break;
      case AccessReportStatusEnum.Succeeded:
        toast.success("Your usage report was successfully created", toastOptions);
        break;
      default:
        console.error(`Invalid Access Report Status Enum: ${status}`)
        break;
    }
  }

  public hideSidePanel = (): void => {
    if (this.state.sidePanelVisible) {
      this.setState({
        sidePanelVisible: false,
      });
    }
  }

  private fetchAccessReports = async (
    isSorting = false,
    isFiltering = false,
    offset: number = this.state.offset,
    sortedColumn: keyof AccessReportsPanelWaffleToppings = this.state.sortedColumn,
    sortDirection: SortDirection = this.state.sortDirection,
    reportSearch: string = this.state.reportSearch,
    includeArchived = this.state.includeArchived,
  ): Promise<void> => {
    let accessReports: AccessReport[] = this.state.accessReports;
    let batchCount: number = this.batchCount;
    let totalCount: number = this.state.totalCount;

    if (isSorting && sortedColumn && sortDirection) {
      // if sorting, return the same amount of records that are currently displayed,
      // without the need for any offsets
      batchCount = this.state.accessReports.length;
      offset = 0;
    }

    if (isFiltering) {
      offset = 0;
    }

    if (isSorting && (sortDirection === SortDirection.None || sortDirection === SortDirection.Unknown)) {
      // this occurs when we're undoing a sort
      sortedColumn = null;
      sortDirection = SortDirection.Unknown;
    }

    const accessReportsDashboardModel: AccessReportsDashboardResponseModel = await this.accessReportsService.getAccessReports(
      batchCount,
      offset,
      reportSearch,
      includeArchived,
      sortDirection,
      sortedColumn,
    );

    if (accessReportsDashboardModel) {
      if (isSorting || isFiltering) {
        // sorting or filtering, meaning we should just replace the old records with the new ones
        accessReports = accessReportsDashboardModel.accessReports;
      } else {
        // acquiring more records (either on load, or on load more click),
        // so the new batch needs to be appended
        accessReports = accessReports.concat(accessReportsDashboardModel.accessReports);
      }

      offset = offset + batchCount;

      totalCount = accessReportsDashboardModel.totalCount;
    }

    let isFiltered: boolean = this.state.isFiltered;
    if (isFiltering && reportSearch) {
      // for the purpose of displaying the "load more" button
      isFiltered = true;
    }

    this.setState({
      accessReports,
      includeArchived,
      isFiltered,
      loading: false,
      offset,
      totalCount,
    });
  }

  private getColumnConfig = (user: UserInfo): ColumnConfig => {
    const columns: ColumnConfig = {
      clone: {
        displayText: "Clone",
        noSort: true,
        testId: "clone",
      },
      companyName: {
        displayText: "Company Name",
        testId: "companyName",
      },
      createdBy: {
        displayText: "Created By",
        hidden: !user.roles.includes(Roles.ACCESS_REPORTS_ADMIN),
        testId: "createdBy",
      },
      dateCreated: {
        displayText: "Date Created",
        testId: "dateCreated",
      },
      download: {
        displayText: "Download",
        noSort: true,
        testId: "download",
      },
      internalClientId: {
        displayText: "Internal Client Identifier",
        testId: "internalClientId",
      },
      product: {
        displayText: "Product",
        testId: "product",
      },
      reportTitle: {
        displayText: "Report Title",
        testId: "reportTitle",
      },
      status: {
        displayText: "Status",
        testId: "status",
      },
    };

    return columns;
  }

  private buildWaffleToppings = (accessReports: AccessReport[]): AccessReportsPanelWaffleToppings[] => {
    const waffleToppings: AccessReportsPanelWaffleToppings[] = accessReports.map((ar, index) => {
      const key: string = ar.accessReportId.toString();

      const reportTitle: React.ReactNode = (
        <Link
          className="font-weight-bold"
          id={`access-reports-dashboard__dashboard--report-title-link-${index}`}
          to={{
            pathname: `${this.props.match.url}/overview/${ar.accessReportId}`,
          }}
        >
          {!!ar.reportTitle ? ar.reportTitle : `Access Report ${ar.accessReportId}`}
        </Link>
      );

      const internalClientId: string = ar.internalClientId;

      const product: string = ar.productName;

      const companyName: string = ar.companyName;

      const dateCreated: string = formatUnconvertedDateCustom(ar.dateCreated, "YYYY-MM-DD hh:mmA");

      const createdBy: string = ar.createdBy;

      const status: string = ar.status;

      const download: React.ReactNode = ar.status === AccessReportStatusEnum.Succeeded && (
        <React.Fragment>
          <TooltipWrapper text="Download" triggerWidth="100%">
            <QESButton
              qesTestId={`access-reports-dashboard__dashboard--pdf-download-${index}`}
              qesType="secondary"
              onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(ar, true, "PDF"); }}
              className="font-weight-bold p-0"
            >
              PDF
            </QESButton>
          </TooltipWrapper>

          <TooltipWrapper containerClassName="ml-3" text="Download" triggerWidth="100%">
            <QESButton
              qesTestId={`access-reports-dashboard__dashboard--excel-download-${index}`}
              qesType="secondary"
              onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(ar, true, "XLSX"); }}
              className="font-weight-bold p-0"
            >
              Excel
            </QESButton>
          </TooltipWrapper>
        </React.Fragment>
      );

      const clone: React.ReactNode = (
        <Link
          id={`access-reports-dashboard__dashboard--clone-${index}`}
          className="font-weight-bold"
          to={{
            pathname: `${this.props.match.path}/wizard/1`,
            state: {
              accessReportId: ar.accessReportId,
            },
          }}
        >
          Clone
        </Link>
      );

      return new AccessReportsPanelWaffleToppings(key, reportTitle, internalClientId, product, companyName, dateCreated, createdBy, status, download, clone);
    });

    return waffleToppings;
  }

  private customSort = (records: AccessReportsPanelWaffleToppings[], column: keyof AccessReportsPanelWaffleToppings, direction: string): AccessReportsPanelWaffleToppings[] => {
    let sortDirection: SortDirection = null;
    switch (direction) {
      case SortDirections.Asc:
        sortDirection = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        sortDirection = SortDirection.Descending;
        break;
      case SortDirections.None:
        sortDirection = SortDirection.None;
        break;

      default:
        sortDirection = SortDirection.Unknown;
    }

    this.fetchAccessReports(true, false, this.state.offset, column, sortDirection, this.state.reportSearch, this.state.includeArchived);

    return records;
  }

  private handleNotifications = async (message: Message): Promise<void> => {
    switch (message?.notificationType) {
      case MessageType.AccessReportsRunStatusChange:
        const projectRunStatusChangePayload: AccessReportRunStatusChangeNotificationMessagePayload = message.payload as AccessReportRunStatusChangeNotificationMessagePayload;

        if (!!projectRunStatusChangePayload) {
          const accessReports: AccessReport[] = this.state.accessReports;
          const accessReport: AccessReport = accessReports.find(x => x.accessReportId === projectRunStatusChangePayload.accessReportId);

          if (!!accessReport) {
            accessReport.status = projectRunStatusChangePayload.status;

            this.setState({
              accessReports,
            });
          }
        }

        break;
      case MessageType.AccessReportsUsageReportStatusChange:
        const usageReportStatusChangePayload: AccessReportUsageReportChangeNotificationMessagePayload = message.payload as AccessReportUsageReportChangeNotificationMessagePayload;

        if (!!usageReportStatusChangePayload) {
          this.handleUsageReportToast(usageReportStatusChangePayload.status);
        }

        break;
      default:
        break;
    }
  }

  private onSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      includeArchived: this.state.includeArchived,
      reportSearch: event.target.value,
    });
  }

  private onClearAllClick = (): void => {
    this.setState({
      isFiltered: false,
      reportSearch: "",
    });

    this.fetchAccessReports(false, true, this.state.offset, null, SortDirection.Unknown, "");
  }

  private renderUserReportSidePanel = (clientId: number, username: string): JSX.Element => {
    return (
      <UsageReport
        clientId={clientId}
        handleUsageReportToast={this.handleUsageReportToast}
        username={username}
      />
    );
  }

  private setArchiveMode = async () => {
    event.preventDefault();

    if (!this.state.loading) {
      this.setState((prevState) => ({
        ...prevState,
        includeArchived: !prevState.includeArchived,
        isFiltered: prevState.isFiltered,
      }));

      await this.fetchAccessReports(true, this.state.isFiltered, 0, this.state.sortedColumn, this.state.sortDirection, this.state.reportSearch, !this.state.includeArchived);
    }
  }
}

const WrappedAccessReportsDashboard = withNotificationContext(AccessReportsDashboard);

export default WrappedAccessReportsDashboard as React.ComponentClass<AccessReportsDashboardProps, AccessReportsDashboardState>;
